/** One day's report, under the calendar (`/calendar?d=YYYY-MM-DD`).
 *
 *  Totals with day-over-day and seven-day-average comparisons, then the apps
 *  and the countries that earned that day. The comparisons are for finished
 *  days: today is still being revised by AdMob, so it says "집계 중" and is
 *  compared with nothing. A day without a row earned nothing, so it counts
 *  as $0 in the seven-day average. Spend, profit and ROAS follow the spend
 *  rule (lib/spend.ts); the calendar above says the reason for a dash.
 *
 *  The tiles are the day's totals (every campaign's spend, by=day); the app
 *  table is per app (mapped spend only, by=app). Spend tied to no app is said
 *  under the tiles. On the phone the app list shows the top ten and opens the
 *  rest on request. */

import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import type { StatsMetrics, StatsRow } from '@/api/client'
import { addDays, dayTitle } from '@/lib/dates'
import { countryName } from '@/lib/country'
import { count, percent, roas as roasText, usd } from '@/lib/money'
import { useStats } from '@/lib/queries'
import { useSpend } from '@/lib/useSpend'
import { platformKey, statusOf } from '@/lib/status'
import {
  Button,
  Change,
  CountryTag,
  DataTable,
  EmptyState,
  ErrorState,
  IconButton,
  Money,
  Skeleton,
  StatTile,
  TonePill,
  UnmappedSpend,
  Won,
  useIsDesktop,
  type Column,
} from '@/ui'

/** Apps a phone shows before 전부 보기. */
export const PHONE_APPS = 10

const ZERO: StatsMetrics = {
  revenue: 0, cost: 0, profit: 0, roas: 0, impressions: 0, clicks: 0,
  ad_requests: 0, matched_requests: 0, ctr: 0, ecpm: 0, match_rate: 0,
}

/** The day's totals and what it is compared with, from the daily rows of
 *  the eight days up to it. */
export function dayFigures(rows: StatsRow[] | undefined, date: string): { day: StatsMetrics; prev: number; avg7: number } {
  const byDate = new Map((rows ?? []).map((r) => [r.date, r]))
  const day: StatsMetrics = byDate.get(date) ?? ZERO
  const prev = byDate.get(addDays(date, -1))?.revenue ?? 0
  const avg7 = Array.from({ length: 7 }, (_, i) => byDate.get(addDays(date, -(i + 1)))?.revenue ?? 0).reduce((a, b) => a + b, 0) / 7
  return { day, prev, avg7 }
}

export function DayReport({ date, today, onShift, onClose }: { date: string; today: string; onShift: (delta: number) => void; onClose: () => void }) {
  const ref = useRef<HTMLElement>(null)
  useEffect(() => {
    ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }, [date])

  const spend = useSpend()
  const desktop = useIsDesktop()
  const [allApps, setAllApps] = useState(false)
  useEffect(() => setAllApps(false), [date])
  const week = useStats({ by: 'day', from: addDays(date, -7), to: date })
  const apps = useStats({ by: 'app', from: date, to: date })
  const geo = useStats({ by: 'country', from: date, to: date, limit: 10 })

  const { day, prev, avg7 } = dayFigures(week.data?.rows, date)
  const isToday = date === today
  const appRows: StatsRow[] = apps.data?.rows ?? []
  const cut = !desktop && !allApps && appRows.length > PHONE_APPS
  const appShown = cut ? appRows.slice(0, PHONE_APPS) : appRows
  const geoRows: StatsRow[] = geo.data?.rows ?? []
  const loading = week.isLoading || apps.isLoading || geo.isLoading
  const error = week.error ?? apps.error ?? geo.error
  const { shown, hint } = spend

  const appColumns: Column<StatsRow>[] = [
    {
      key: 'app',
      header: '앱',
      card: 'title',
      cell: (a) => (
        <span className="block min-w-0">
          <span className="block max-w-[18rem] truncate font-medium" title={a.app_name}>
            {a.app_name}
          </span>
          <span className="block text-meta text-muted-foreground">{statusOf('platform', platformKey(a.platform)).label}</span>
        </span>
      ),
    },
    { key: 'revenue', header: '수익', align: 'right', card: 'hidden', cell: (a) => <Money value={a.revenue} krw /> },
    { key: 'cost', header: '지출', align: 'right', card: 'hidden', cell: (a) => <Money value={a.cost} hidden={!shown} hint={hint} /> },
    { key: 'impressions', header: '노출', align: 'right', card: 'hidden', cell: (a) => <span className="tabular-nums">{count(a.impressions)}</span> },
    { key: 'clicks', header: '클릭', align: 'right', card: 'hidden', cell: (a) => <span className="tabular-nums">{count(a.clicks)}</span> },
    { key: 'ecpm', header: 'eCPM', align: 'right', card: 'hidden', cell: (a) => <Money value={a.ecpm} /> },
    { key: 'match', header: '매칭률', align: 'right', card: 'hidden', cell: (a) => <span className="tabular-nums">{a.ad_requests > 0 ? percent(a.match_rate) : '—'}</span> },
    // The phone's card: one line under the name instead of six label rows.
    {
      key: 'summary',
      header: '요약',
      cardOnly: true,
      card: 'sub',
      cell: (a) => (
        <span className="tabular-nums">
          수익 <span className="whitespace-nowrap text-foreground">{usd(a.revenue)}</span><Won usd={a.revenue} className="ml-1" />
          {shown && (
            <>
              {' · '}지출 <span className="whitespace-nowrap text-foreground">{usd(a.cost)}</span>
            </>
          )}
          {' · '}노출 {count(a.impressions)} · eCPM {usd(a.ecpm)}
        </span>
      ),
    },
  ]
  const geoColumns: Column<StatsRow>[] = [
    {
      key: 'country',
      header: '국가',
      card: 'title',
      cell: (c) => (
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium">{countryName(c.country)}</span>
          <CountryTag code={c.country ?? ''} />
        </span>
      ),
    },
    { key: 'revenue', header: '수익', align: 'right', card: 'sub', cell: (c) => <Money value={c.revenue} krw /> },
    {
      key: 'share',
      header: '비중',
      align: 'right',
      card: 'meta',
      cell: (c) => <span className="tabular-nums">{day.revenue > 0 ? percent(c.revenue / day.revenue) : '—'}</span>,
    },
  ]

  return (
    <section ref={ref} aria-label="일별 리포트" className="scroll-mt-4 space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <IconButton label="전날" icon={ChevronLeft} variant="secondary" onClick={() => onShift(-1)} />
          <div>
            <p className="text-meta text-muted-foreground">일별 리포트</p>
            <h2 className="text-section font-semibold tabular-nums">{dayTitle(date, today)}</h2>
          </div>
          <IconButton label="다음날" icon={ChevronRight} variant="secondary" disabled={date >= today} onClick={() => onShift(1)} />
          {isToday && <TonePill tone="publishing" label="집계 중" />}
        </div>
        <IconButton label="닫기" icon={X} onClick={onClose} />
      </div>

      {error ? (
        <ErrorState title="일별 리포트를 불러오지 못했다" error={error} onRetry={() => { week.refetch(); apps.refetch(); geo.refetch() }} />
      ) : loading ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <StatTile label="수익" value={<Money value={day.revenue} krw />} aside={isToday ? <TonePill tone="publishing" label="집계 중" /> : undefined}>
              {!isToday && (
                <>
                  <div>
                    <Change label="전일 대비" now={day.revenue} base={prev} />
                  </div>
                  <div>
                    <Change label={`7일 평균 ${usd(avg7)} 대비`} now={day.revenue} base={avg7} />
                  </div>
                </>
              )}
            </StatTile>
            <StatTile
              label="순이익"
              value={<Money value={day.profit} hidden={!shown} hint={hint} />}
              hint={shown ? `지출 ${usd(day.cost)} · ROAS ${roasText(day.roas, day.cost)}` : undefined}
            />
            <StatTile label="노출" value={<span>{count(day.impressions)}</span>} hint={`클릭 ${count(day.clicks)} · CTR ${day.impressions > 0 ? percent(day.ctr) : '—'}`} />
            <StatTile label="eCPM" value={<Money value={day.ecpm} />} hint="노출 1천 회당 수익" />
            <StatTile
              label="매칭률"
              value={day.ad_requests > 0 ? percent(day.match_rate) : '—'}
              hint={`요청 ${count(day.ad_requests)} · 매칭 ${count(day.matched_requests)}`}
            />
          </div>
          <UnmappedSpend amount={apps.data?.unmapped_cost} shown={shown} className="-mt-2" />

          {appRows.length === 0 ? (
            <EmptyState title="이 날은 광고 수익 데이터가 없다" />
          ) : (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <div className="min-w-0 space-y-2 xl:col-span-2">
                <h3 className="text-section font-semibold">
                  앱
                  <span className="ml-2 text-meta font-normal text-muted-foreground">
                    {cut ? `상위 ${PHONE_APPS} / ${appRows.length}개` : `${appRows.length}개`}{shown ? " · 지출은 앱에 연결된 캠페인만" : ""}
                  </span>
                </h3>
                <DataTable label="그날의 앱" columns={appColumns} rows={appShown} rowKey={(a) => a.app_id ?? a.app_name ?? ''} />
                {cut && (
                  <Button className="w-full" onClick={() => setAllApps(true)}>
                    전부 보기 · {appRows.length}개
                  </Button>
                )}
              </div>
              <div className="min-w-0 space-y-2">
                <h3 className="text-section font-semibold">
                  국가<span className="ml-2 text-meta font-normal text-muted-foreground">상위 10</span>
                </h3>
                <DataTable label="그날의 국가" columns={geoColumns} rows={geoRows} rowKey={(c) => c.country ?? ''} />
              </div>
            </div>
          )}
        </>
      )}
    </section>
  )
}
